// frontend/src/ClubSalesHistory/ClubSalesHistory.jsx — /club-sales-history: BOPIS and in-stadium gross sales per home game,
// with today's game live. Spec: docs/superpowers/specs/2026-10-21-club-sales-history-design.md.
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cshApi, download } from './apiClient';

const TEAMS = [['spirit', 'Spirit'], ['gotham', 'Gotham']];
const LIVE_POLL_MS = 120000;

const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;
const num = (n) => (Number(n) || 0).toLocaleString('en-US');
const day = (iso) => new Date(`${iso}T12:00:00`);
const fmtDate = (iso) => day(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const shortDate = (iso, back = 0) => { const d = day(iso); d.setDate(d.getDate() - back); return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
const fmtTime = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
function fmtKick(hhmm) {
  if (!hhmm) return 'kickoff time not on file';
  const [h, m] = hhmm.split(':').map(Number);
  return `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'AM' : 'PM'} ET`;
}

function Seg({ label, value, options, onChange }) {
  return (
    <div className="uh-gc-seg" role="radiogroup" aria-label={label}>
      {options.map(([v, text]) => <button key={v} type="button" role="radio" aria-checked={String(value) === String(v)} onClick={() => onChange(v)}>{text}</button>)}
    </div>
  );
}

function Bars({ values, labels, pos }) {
  const max = Math.max(1, ...values);
  return (
    <div>
      <div className={`uh-csh-chart${pos ? ' pos' : ''}`} role="img" aria-label={labels.map((l, i) => `${l}: ${values[i]}`).join(', ')}>
        {values.map((v, i) => <i key={i} title={`${labels[i]}: ${v}`} style={{ height: `${Math.max(3, Math.round((v / max) * 100))}%` }} />)}
      </div>
      <div className="uh-csh-axis">{labels.map((l, i) => <span key={i}>{labels.length < 9 || i % 2 === 0 ? l : ''}</span>)}</div>
    </div>
  );
}

function Items({ items, pos }) {
  if (!items.length) return <div className="uh-faint">No sales.</div>;
  const max = items[0].gross || 1;
  return (
    <div className={`uh-csh-items${pos ? ' pos' : ''}`}>
      {items.map((it) => (
        <div className="it" key={it.item}>
          <div className="bar"><i style={{ width: `${Math.round((it.gross / max) * 100)}%` }} /><span title={it.item}>{it.item}</span></div>
          <em className="uh-num">{money(it.gross)}<small>{num(it.units)} unit{it.units === 1 ? '' : 's'}</small></em>
        </div>))}
    </div>
  );
}

function Kpis({ c }) {
  return (
    <div className="uh-csh-kpis">
      {[[num(c.orders), 'orders'], [num(c.units), 'units'], [money(c.gross), 'gross sales'], [c.per_order == null ? '—' : money(c.per_order), 'per order']]
        .map(([v, k]) => <div key={k}><b className="uh-num">{v}</b><span>{k}</span></div>)}
    </div>
  );
}

function Detail({ team, row, live }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(() => cshApi.game(team, row.game_date).then(setD, (e) => setError(e.message)), [team, row.game_date]);
  const liveKey = live ? row.as_of : '';
  useEffect(() => { load(); }, [load, liveKey]);   // a live row reloads with each poll

  const exportCsv = async () => { setBusy('csv'); try { download(await cshApi.csv(team, row.game_date)); } catch (e) { setError(e.message); } setBusy(''); };
  const togglePreview = async () => { if (preview) { setPreview(null); return; } try { setPreview(await cshApi.preview(team, row.game_date)); } catch (e) { setError(e.message); } };
  const reread = async () => {
    setBusy('refresh'); setError('');
    try { setD((await cshApi.refresh(team, row.game_date)).game); setPreview(null); } catch (e) { setError(e.message); }
    setBusy('');
  };

  if (error && !d) return <div className="uh-kpc-banner bad">{error}</div>;
  if (!d) return <div className="uh-empty">Loading…</div>;
  const b = d.bopis; const p = d.in_stadium;
  const dayLabels = [`By ${shortDate(d.game_date, 7)}`, ...[6, 5, 4, 3, 2, 1].map((k) => shortDate(d.game_date, k)), `${shortDate(d.game_date)} (game)`];
  const dayValues = [7, 6, 5, 4, 3, 2, 1, 0].map((k) => b.by_date[k]);
  const hours = []; const hourLabels = [];
  for (let h = 10; h <= 23; h += 1) { hours.push(p.by_hour[h]); hourLabels.push(h > 12 ? `${h - 12}p` : h === 12 ? '12p' : `${h}a`); }
  const late = p.by_hour[0];
  return (
    <div>
      {error && <div className="uh-kpc-banner bad">{error}</div>}
      <div className="uh-csh-cols">
        <div className="uh-csh-panel">
          <h3><span className="uh-csh-dot" />BOPIS pre-orders</h3>
          <Kpis c={b} />
          <div className="uh-csh-cap">Orders placed, by date (ET); the first bar is everything on or before {shortDate(d.game_date, 7)}</div>
          <Bars values={dayValues} labels={dayLabels} />
          <div className="uh-csh-cap">Top items by gross sales</div>
          <Items items={b.top_items} />
          {b.cancelled > 0 && <div className="uh-csh-cap">{b.cancelled} cancelled order{b.cancelled === 1 ? '' : 's'} not counted</div>}
        </div>
        <div className="uh-csh-panel pos">
          <h3><span className="uh-csh-dot pos" />In-stadium (POS), {live ? 'today so far' : 'game day'}</h3>
          <Kpis c={p} />
          <div className="uh-csh-cap">Orders rung up, by hour of the day (ET){late ? `; ${late} after midnight` : ''}{live ? `; live, as of ${fmtTime(row.as_of)}` : ''}</div>
          <Bars values={hours} labels={hourLabels} pos />
          <div className="uh-csh-cap">Top items by gross sales</div>
          <Items items={p.top_items} pos />
        </div>
      </div>
      <div className="uh-csh-exp">
        <button type="button" className="uh-btn primary slim" disabled={busy === 'csv'} onClick={exportCsv}>{live ? 'Export orders so far (CSV)' : 'Export orders (CSV)'}</button>
        <button type="button" className="uh-btn slim" onClick={togglePreview}>{preview ? 'Hide' : 'Preview'} the file</button>
        {!live && <button type="button" className="uh-btn slim" disabled={busy === 'refresh'} onClick={reread}>{busy === 'refresh' ? 'Re-reading…' : 'Re-read from Shopify'}</button>}
        <span className="uh-csh-cap">{num(b.orders + p.orders)} orders, one row per item. Price and Total are gross: before discounts and tax.</span>
      </div>
      {preview && (
        <div className="uh-csh-preview uh-tscroll"><table className="uh-tbl">
          <thead><tr>{preview.columns.map((c, i) => <th key={c} className={i >= 5 ? 'r' : ''}>{c}</th>)}</tr></thead>
          <tbody>{preview.rows.map((r, i) => <tr key={i}>{r.map((v, j) => <td key={j} className={j >= 5 ? 'r uh-num' : j === 0 ? 'uh-mono' : ''}>{v}</td>)}</tr>)}
            {!preview.rows.length && <tr><td colSpan={8}><div className="uh-empty">No orders.</div></td></tr>}</tbody>
        </table><div className="uh-csh-cap" style={{ padding: '8px 12px' }}>First {preview.rows.length} of {num(preview.total)} rows.</div></div>
      )}
    </div>
  );
}

function GameRow({ team, row, open, onToggle }) {
  const live = !!row.live;
  const tot = row.total_gross; const bp = tot ? (row.bopis.gross / tot) * 100 : 0;
  const toggle = (e) => { if (e.type === 'click' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } };
  return (
    <>
      <tr className={`uh-csh-row${live ? ' live' : ''}`} tabIndex={0} role="button" aria-expanded={open} onClick={toggle} onKeyDown={toggle}>
        <td>
          <span className="uh-csh-chev" aria-hidden="true">▸</span>
          <span className="nm">vs {row.opponent}</span>{' '}
          <span className="uh-csh-when">{live ? 'Today' : fmtDate(row.game_date)} · {fmtKick(row.kickoff_time)}</span>
          {live && <span className="uh-csh-live"><i />LIVE · {fmtTime(row.as_of)}</span>}
          {row.theme && <div><span className="uh-csh-theme" title="Theme of the match">{row.theme}</span></div>}
        </td>
        <td className="r uh-num">{num(row.bopis.units)}</td>
        <td className="r uh-num">{money(row.bopis.gross)}</td>
        <td className="r uh-num">{num(row.in_stadium.units)}</td>
        <td className="r uh-num">{money(row.in_stadium.gross)}</td>
        <td className="r uh-num"><b>{money(tot)}</b></td>
        <td><div className="uh-csh-split" title={`BOPIS ${Math.round(bp)}%`}><i className="b" style={{ width: `${bp}%` }} /><i className="p" style={{ width: `${100 - bp}%` }} /></div></td>
      </tr>
      {open && <tr className="uh-csh-det"><td colSpan={7}><Detail team={team} row={row} live={live} /></td></tr>}
    </>
  );
}

export default function ClubSalesHistory() {
  const [params, setParams] = useSearchParams();
  const team = params.get('team') === 'gotham' ? 'gotham' : 'spirit';
  const season = params.get('season') || '';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});

  const load = useCallback(() => cshApi.games(team, season).then((d) => { setData(d); setError(''); }, (e) => setError(e.message)), [team, season]);
  useEffect(() => { setData(null); setOpen({}); load(); }, [load]);
  useEffect(() => {   // the live row refreshes every 2 minutes while the tab is visible
    if (!data || !data.live) return undefined;
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, LIVE_POLL_MS);
    return () => clearInterval(t);
  }, [data, load]);
  useEffect(() => { if (data && data.live) setOpen((o) => (data.live.game_date in o ? o : { ...o, [data.live.game_date]: true })); }, [data]);

  const set = (k, v) => { const p = new URLSearchParams(params); p.set(k, v); if (k === 'team') p.delete('season'); setParams(p, { replace: true }); };
  const rows = data ? [...(data.live ? [data.live] : []), ...data.games] : [];
  const strip = data && data.strip;
  return (
    <section className="uh-page">
      <div className="uh-ph">
        <div>
          <h1 className="uh-h1">Club Store Sales History</h1>
          <div className="uh-sub">Every home game played, with BOPIS pre-orders and the in-stadium sales rung up on game day. All sales are gross: before discounts, tax and shipping.</div>
        </div>
        {strip && (
          <div className="uh-ia-stats">
            {[[data.live ? `${strip.games - 1} + today` : num(strip.games), data.live ? 'home games, today live' : 'home games played'],
              [money(strip.bopis_gross), 'BOPIS gross sales'], [money(strip.in_stadium_gross), 'in-stadium gross sales'],
              [money(strip.total_gross), `${data.season} total gross sales`]]
              .map(([v, k]) => <div className="stat" key={k}><span className="v uh-num">{v}</span><span className="k">{k}</span></div>)}
          </div>
        )}
      </div>
      {error && <div className="uh-kpc-banner bad">{error}</div>}
      {data && data.live && data.live.read_error && <div className="uh-kpc-banner warn">Couldn't reach Shopify for the latest sales ({data.live.read_error}); showing what was read at {fmtTime(data.live.as_of)}.</div>}
      <div className="uh-card">
        <div className="uh-card-h">
          <Seg label="Team" value={team} options={TEAMS} onChange={(v) => set('team', v)} />
          {data && <Seg label="Season" value={data.season} options={data.seasons.map((s) => [s, String(s)])} onChange={(v) => set('season', v)} />}
          <span className="uh-sp" />
          {data && data.live && <span className="uh-csh-cap">Live until {fmtTime(data.live.live_until)} · refreshes every 2 min</span>}
        </div>
        <div className="uh-tscroll"><table className="uh-tbl uh-csh-tbl">
          <thead><tr><th>Game</th><th className="r">BOPIS units</th><th className="r">BOPIS gross sales</th><th className="r">In-stadium units</th><th className="r">In-stadium gross sales</th><th className="r">Total gross sales</th><th>BOPIS / in-stadium</th></tr></thead>
          <tbody>
            {!data && !error && <tr><td colSpan={7}><div className="uh-empty">Loading…</div></td></tr>}
            {rows.map((r) => <GameRow key={r.game_date} team={team} row={r} open={!!open[r.game_date]} onToggle={() => setOpen((o) => ({ ...o, [r.game_date]: !o[r.game_date] }))} />)}
            {data && !rows.length && (
              <tr><td colSpan={7}><div className="uh-empty">No sales on file for {data.label} in {data.season}
                {data.scheduled ? `: ${data.scheduled} home games on the schedule, but the store has no BOPIS or in-stadium orders from that season.` : ': no home games on the schedule.'}</div></td></tr>)}
          </tbody>
        </table></div>
        {data && data.waiting.length > 0 && (
          <div className="uh-csh-cap" style={{ padding: '8px 16px' }}>
            Not read yet: {data.waiting.map((w) => `vs ${w.opponent} (${shortDate(w.game_date)})`).join(', ')}. Played games load overnight.
          </div>)}
      </div>
      <div className="uh-kpc-banner info uh-csh-note">
        <b>How the numbers are found.</b> BOPIS: orders tagged BOPIS, not cancelled, on the next home game on or after the day they were placed (so pre-season sales land on the opener). In-stadium: every Shopify POS order rung up on game day, New York time, with the day running to 1 AM; a sales day a day off the schedule's date still lands on the game. Kickoff times and match themes come from each club's schedule. On game day the game is live at the top until 5 hours after kickoff, refreshing every 2 minutes; every game is re-read overnight for a week.
      </div>
    </section>
  );
}
